import { ChangeEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Label as InputLabel } from 'src/components/FormComponents/Input';
import { TextIcon } from 'src/components/Icons/TextIcon';
import { DocumentsIcon } from 'src/components/Icons/icons';
import { useNotification } from 'src/components/Notification/useNotfication';
import { isAllowedMessageAttachment } from './messageAttachmentUpload';

type MessageAttachmentsFieldProps = {
    attachments: File[];
    onChange: (files: File[]) => void;
    disabled?: boolean;
    hideLabel?: boolean;
};

export const MessageAttachmentsField = ({
    attachments,
    onChange,
    disabled,
    hideLabel,
}: MessageAttachmentsFieldProps) => {
    const { t } = useTranslation();
    const notify = useNotification();
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const handleAttachmentsChange = (event: ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files ?? []);
        if (!files.length) {
            return;
        }

        const validFiles = files.filter(isAllowedMessageAttachment);
        if (validFiles.length !== files.length) {
            notify(
                t(
                    'modules.messages.form.unsupportedAttachmentType',
                    'Only PDF, image, or video files are allowed',
                ),
                true,
            );
        }

        if (validFiles.length) {
            onChange([...attachments, ...validFiles]);
        }

        if (event.target) {
            event.target.value = '';
        }
    };

    const handleRemoveAttachment = (index: number) => {
        onChange(
            attachments.filter((_, currentIndex) => currentIndex !== index),
        );
    };

    return (
        <div>
            {!hideLabel && (
                <InputLabel
                    label={t('modules.messages.form.attachmentsLabel')}
                />
            )}
            <button
                type="button"
                className="primary-btn-small flex items-center gap-1 pl-2 pr-4 py-0.5"
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled}
            >
                <TextIcon SVG={DocumentsIcon} />
                <span>{t('common.upload')}</span>
            </button>
            <input
                type="file"
                multiple
                accept="application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif,video/*"
                onChange={handleAttachmentsChange}
                ref={fileInputRef}
                disabled={disabled}
                className="hidden"
            />
            {attachments.length > 0 && (
                <ul className="mt-2 flex flex-col gap-1 text-sm text-mongooseGray">
                    {attachments.map((file, index) => (
                        <li
                            key={`${file.name}-${file.lastModified}-${index}`}
                            className="flex items-center gap-2"
                        >
                            <span className="truncate">{file.name}</span>
                            <button
                                type="button"
                                className="text-venetianRed underline"
                                onClick={() => handleRemoveAttachment(index)}
                                disabled={disabled}
                            >
                                {t('common.remove')}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};
